import { queryClient } from "@/constants"
import { CLIENT_API } from "@/services/axios-client"
import { useNotificationStore } from "@/states/zustand/notification"
import { useMutation, useQuery } from "@tanstack/react-query"

const mutationErrorHandler = (error: string) => {
    useNotificationStore.getState().setNotification({
        type: "error",
        message: error
    })
}

export const useGetRooms = (isEnabled?: boolean) => {
    return useQuery({
        queryKey: ['rooms'],
        queryFn: CLIENT_API.getRooms,
        staleTime: 5 * 60 * 1000,
        enabled: isEnabled
    })
}

export const useCreateNewRoom = () => {
    return useMutation({
        mutationKey: ['create-room'],
        mutationFn: (data: CreateRoomRequest[]) => CLIENT_API.createNewRoom(data),
        onSuccess: (data) => {
            queryClient.setQueryData(
                ['rooms'],
                (oldData: Room[]) => {
                    return [...oldData, ...data,]
                }
            );
        },
        onError: mutationErrorHandler
    })
}

export const useUpdateRoom = () => {
    return useMutation({
        mutationKey: ['update-room'],
        mutationFn: ({ id, data }: { id: number, data: CreateRoomRequest }) => CLIENT_API.updateRoom(id, data),
        onSuccess: (data, { id }) => {
            queryClient.setQueryData(
                ['rooms'],
                (oldData: Room[]) => {
                    return oldData?.map((it) => {
                        if (it.id === id) {
                            return { ...it, ...data }
                        }

                        return it;
                    })
                }
            );
            useNotificationStore.getState().setNotification({
                type: 'success',
                message: 'Room updated successfully!'
            })
        },
        onError: mutationErrorHandler
    })
}

export const useDeleteRoom = () => {
    return useMutation({
        mutationKey: ['delete-room'],
        mutationFn: (id: number) => CLIENT_API.deleteRoom(id),
        onSuccess: (_, id) => {
            queryClient.setQueryData(
                ['rooms'],
                (oldData: Room[]) => {
                    return oldData?.filter(it => it.id !== id)
                }
            );
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Room deleted successfully!"
            })
        },
        onError: mutationErrorHandler
    });
}

export const useGetPendingRoomRequest = (roomId: number | null, isEnabled: boolean = true) => {
    return useQuery({
        queryKey: ['pending-room-request', roomId],
        queryFn: () => CLIENT_API.getPendingRoomRequest(roomId!),
        staleTime: 5 * 60 * 1000,
        enabled: !!roomId && isEnabled
    })
}

export const useGetAcceptedMeditatorByRoom = (roomId: number, isEnabled: boolean = true) => {
    return useQuery({
        queryKey: ['accepted-meditator-by-room', roomId],
        queryFn: () => CLIENT_API.getAcceptedMeditatorByRoomId(roomId),
        enabled: !!roomId && isEnabled,
        refetchOnWindowFocus: false
    })
}

export const useAcceptRejectRoomRequest = () => {
    return useMutation({
        mutationKey: ['accept-reject-room-request'],
        mutationFn: ({ roomId, userId, action }: { roomId: number, userId: number, action: 'accept' | 'reject' }) => {
            switch (action) {
                case 'accept':
                    return CLIENT_API.acceptRoomRequest(roomId, userId);
                case 'reject':
                    return CLIENT_API.rejectRoomRequest(roomId, userId);
            }
        },
        onSuccess: (data, { action, roomId }) => {
            useNotificationStore.getState().setNotification({
                type: "success",
                message: `Room request ${action}ed notification sent to meditator!`
            });
            queryClient.invalidateQueries({
                queryKey: ['pending-room-request', roomId]
            })
            queryClient.invalidateQueries({
                queryKey: ['rooms']
            })
        },
        onError: mutationErrorHandler
    })
}

// waiting room
export const useGetWaitingMeditators = (isEnabled: boolean = true) => {
    return useQuery({
        queryKey: ['waiting-meditators'],
        queryFn: CLIENT_API.getWaitingMeditators,
        enabled: isEnabled,
    })
}

export const useChangeMeditatorRoom = () => {
    return useMutation({
        mutationKey: ['change-room'],
        mutationFn: ({ roomId, userId }: { roomId: number, userId: number }) => CLIENT_API.changeMeditatorRoom(roomId, userId),
        onSuccess: (_, { userId, roomId }) => {
            useNotificationStore.getState().setNotification({
                type: "success",
                message: "Operation success!"
            });
            queryClient.setQueryData(
                ['waiting-meditators'],
                (oldData: RoomRequestUser[]) => {
                    return oldData?.filter(it => it.userId !== userId);
                }
            );
            queryClient.setQueryData(
                ['rooms'],
                (oldData: Room[]) => {
                    return oldData?.map((it) => {
                        if (it.id === roomId) {
                            return { ...it, count: it.count + 1 }
                        };
                        return it;
                    })
                }
            );
            queryClient.setQueryData(
                ['room-detail', roomId],
                (oldData: Room) => {
                    return {
                        ...oldData,
                        count: oldData.count + 1
                    } as Room;
                }
            )
            queryClient.invalidateQueries({
                queryKey: ['accepted-meditator-by-room', roomId]
            })
        },
        onError: mutationErrorHandler
    })
}

export const useGetRoomsByMeditationEvent = (id: number, isEnabled: boolean = true) => {
    return useQuery({
        queryKey: ['rooms-by-meditation-event', id],
        queryFn: () => CLIENT_API.getRoomsByMeditationEvent(id),
        enabled: !!id && isEnabled,
        staleTime: 5 * 60 * 1000
    })
}
export const useGetRoomDetail = (roomId: number, isEnabled: boolean = true) => {
    return useQuery({
        queryKey: ['room-detail', roomId],
        queryFn: () => CLIENT_API.getRoomDetail(roomId),
        enabled: !!roomId && isEnabled
    })
}

export const useMoveMeditatorToWaitingRoom = () => {
    return useMutation({
        mutationKey: ['move-to-waiting-room'],
        mutationFn: ({ roomId, userId }: { roomId: number, userId: number }) => CLIENT_API.moveMeditatorToWaitingRoom(roomId, userId),
        onSuccess: (data, { roomId, userId }) => {
            useNotificationStore.getState().setNotification({
                type: "success",
                message: data.message
            })
            queryClient.setQueryData(
                ['room-detail', roomId],
                (oldData: Room) => {
                    return {
                        ...oldData,
                        count: oldData.count - 1
                    } as Room;
                }
            );
            queryClient.setQueryData(
                ['accepted-meditator-by-room', roomId],
                (oldData: RoomRequestUser[]) => {
                    return oldData?.filter(it => it.userId !== userId)
                }
            );
            queryClient.setQueryData(
                ['rooms'],
                (oldData: Room[]) => {
                    return oldData?.map((it) => {
                        if (it.id === roomId) {
                            return {
                                ...it,
                                count: it.count - 1
                            }
                        };

                        return it;
                    })
                }
            );
        },
        onError: mutationErrorHandler
    })
}

export const useAssignMeditatorToRoom = () => {
    return useMutation({
        mutationKey: ['assign-meditator'],
        mutationFn: ({ roomId, userId }: { roomId: number, userId: number }) => CLIENT_API.assignMeditatorToRoom(roomId, userId),
        onSuccess: (data, { roomId }) => {
            useNotificationStore.getState().setNotification({
                type: "success",
                message: data?.message
            });
            queryClient.setQueryData(
                ['room-detail', roomId],
                (oldData: Room) => {
                    return {
                        ...oldData,
                        count: oldData.count + 1
                    } as Room;
                }
            );
            queryClient.setQueryData(
                ['rooms'],
                (oldData: Room[]) => {
                    return oldData?.map((it) => {
                        if (it.id === roomId) {
                            return { ...it, count: it.count + 1 }
                        };
                        return it;
                    })
                }
            );
            queryClient.invalidateQueries({
                queryKey: ['accepted-meditator-by-room', roomId]
            })
        },
        onError: mutationErrorHandler
    })
}

export const useAssignRoomsToMeditationRetreat = () => {
    return useMutation({
        mutationKey: ['assign-rooms-to-retreat'],
        mutationFn: ({ retreatId, roomIds }: { retreatId: number, roomIds: number[] }) => CLIENT_API.assignRoomsToMeditationRetreat(retreatId, roomIds),
        onSuccess: (data, { retreatId }) => {
            queryClient.setQueryData(
                ['rooms-by-meditation-event', retreatId],
                () => {
                    return data.rooms;
                }
            );
            queryClient.invalidateQueries({
                queryKey: ['rooms']
            });
        },
        onError: mutationErrorHandler
    })
}


// announce
export const useAnnounceRoomSelection = () => {
    return useMutation({
        mutationKey: ["announce-room-selection"],
        mutationFn: (retreatId: number) => CLIENT_API.announceRoomSelection(retreatId),
        onSuccess: () => {
            useNotificationStore.getState().setNotification({
                type: 'success',
                message: `Room selection announcement for this meditation event was sent successfully!`
            })
        },
        onError: mutationErrorHandler
    });
}